<template>
  <div class="relative flex flex-col justify-center w-full space-y-6">
    <div
      @click="showTooltip = !showTooltip"
      class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
    >
      <h2>{{ item.questionDisplay }}</h2>
      <span v-if="item.required" class="text-ruby">*</span>
      <span class="cursor-pointer" v-if="item.tooltip.length"
        ><InformationCircleIcon class="w-8 h-8 text-gold"
      /></span>
    </div>
    <label for="" class="text-sm text-navada">{{ item.placeholder }} </label>
    <form
      v-focus
      class="w-full space-y-6"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
    >
      <div
        id="headlessui-radiogroup-5"
        role="radiogroup"
        data-v-0fecee51=""
        aria-labelledby="headlessui-label-6"
      >
        <label id="headlessui-label-6" class="sr-only" role="none">
          {{ item.questionDisplay }}
        </label>
        <div class="w-full space-y-4" role="none">
          <div
            v-for="option in options"
            :key="option.key"
            @click="setSelected(option)"
            :class="[
              'relative md:w-full block rounded-lg border border-gray-300 bg-white dark:bg-[#1b1c20] shadow-sm px-6 py-4 cursor-pointer hover:border-gold sm:flex sm:justify-between focus:outline-none',
            ]"
            :id="`headlessui-radiogroup-option-${option.key}`"
            role="radio"
            aria-checked="true"
            tabindex="0"
            aria-labelledby="headlessui-label-8"
          >
            <div class="flex items-center">
              <div class="text-sm">
                <p
                  id="headlessui-label-8"
                  class="text-xl font-bold text-gray-900 dark:text-white"
                >
                  {{ option.value }}
                </p>
              </div>
            </div>
            <div
              class="absolute border-2 border-transparent rounded-lg pointer-events-none -inset-px"
              :class="{ 'border-gold': option.selected }"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <button
          v-if="!item.required"
          @click="$emit('skip', item, selected)"
          class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
        >
          <span class="text-black dark:text-white">{{ t("input.skip") }}</span>
          <SkipIcon />
        </button>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
        </div>
      </div>
    </form>

    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { reactive, toRefs } from "vue";
import Spinner from "../Spinner";
import { useI18n } from "vue-i18n";
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/solid";

import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";

const { map, find } = require("lodash");

export default {
  name: "MultiSelect",
  components: {
    Spinner,
    ExclamationCircleIcon,
    InformationCircleIcon,
    SkipIcon,
    ReturnIcon,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const state = reactive({
      showTooltip: false,
      options: [],
      showError: false,
      error: "",
    });

    Object.entries(props.item.choices).forEach(([key, value]) => {
      const item = {
        key,
        value,
        selected: false,
      };
      state.options.push(item);
    });

    const setSelected = (item) => {
      item.selected = !item.selected;

      find(state.options, ["selected", true]);

      /*if (typeof selected === "undefined") {
                state.selected = false
            } else {
                state.selected = true
            }*/
    };

    if (props.item.answer) {
      props.item.answer.answer.forEach((key) => {
        const obj = find(state.options, ["key", key]);
        if (typeof obj === "object") {
          obj.selected = true;
          state.selected = true;
        }
      });
    }

    const next = () => {
      if (props.item.required && !find(state.options, ["selected", true])) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit(
        "next",
        props.item,
        state.options.filter((x) => x.selected === true).map((y) => y.key)
      );
    };
    return {
      t,
      next,
      setSelected,
      ...toRefs(state),
    };
  },
};
</script>
